import { analytics } from '@lib/analytics/page-events';
import { ProductListV2 } from '@lib/types/api/ProductListV2';
import type { ListData } from 'ui-components';

import {
  addItemToList,
  removeItemFromList,
  setFavoriteList,
} from './productListsApi';

export type ProductInfo = { id: string; name: string };

export type ListInfo = { id: string; name: string; slug?: string };

export type SnackbarData = {
  type: 'added' | 'removed';
  listName: string;
  listSlug: string;
  productId: string;
  productName: string;
  key: number;
};

export type SnackbarInfo = {
  type: 'added' | 'removed';
  listName: string;
};

export const toListData = (
  list: ProductListV2,
  personal: boolean = true
): ListData => ({
  id: list.id,
  name: list.name,
  count: list.productCount,
  type: list.visibility,
  personal,
  default: list.isFavorites,
});

export function getListChanges(
  initialListIds: string[],
  selectedLists: ListData[]
) {
  const initialSet = new Set(initialListIds);
  const selectedIdSet = new Set(selectedLists.map((l) => l.id));

  const listsToAdd = selectedLists.filter((l) => !initialSet.has(l.id));
  const idsToRemove = initialListIds.filter((id) => !selectedIdSet.has(id));

  return { listsToAdd, idsToRemove };
}

export function resolveSnackbarInfo({
  listsToAdd,
  listsToRemove,
  intention,
  createdNewList,
  multipleListsLabel,
}: {
  listsToAdd: ListInfo[];
  listsToRemove: ListInfo[];
  intention: 'add' | 'remove';
  createdNewList: ListInfo | null;
  multipleListsLabel: string;
}): SnackbarInfo | null {
  const formatName = (items: ListInfo[]) =>
    items.length === 1
      ? items[0].name
      : multipleListsLabel.replace('{count}', String(items.length));

  if (
    createdNewList &&
    !listsToRemove.some((l) => l.id === createdNewList.id)
  ) {
    return { type: 'added', listName: createdNewList.name };
  }

  if (intention === 'add') {
    if (listsToAdd.length > 0)
      return { type: 'added', listName: formatName(listsToAdd) };
    if (listsToRemove.length > 0)
      return { type: 'removed', listName: formatName(listsToRemove) };
  } else {
    if (listsToRemove.length > 0)
      return { type: 'removed', listName: formatName(listsToRemove) };
    if (listsToAdd.length > 0)
      return { type: 'added', listName: formatName(listsToAdd) };
  }

  return null;
}

export async function syncListChanges({
  listsToAdd,
  idsToRemove,
  productId,
  getOrgIdForList,
}: {
  listsToAdd: ListData[];
  idsToRemove: string[];
  productId: string;
  getOrgIdForList: (listId: string) => string | undefined;
}): Promise<void> {
  await Promise.all([
    ...listsToAdd.map((l) =>
      addItemToList(l.id, productId, getOrgIdForList(l.id))
    ),
    ...idsToRemove.map((id) =>
      removeItemFromList(id, productId, getOrgIdForList(id))
    ),
  ]);
}

export function trackListChangesAnalytics({
  listsToAdd,
  idsToRemove,
  productId,
  productName,
}: {
  listsToAdd: ListData[];
  idsToRemove: string[];
  productId: string;
  productName: string;
}): void {
  listsToAdd.forEach(() =>
    analytics.pushSaveToListEvent({
      asset_id: productId,
      asset_name: productName,
    })
  );
  idsToRemove.forEach(() =>
    analytics.pushRemoveFromListEvent({
      asset_id: productId,
      asset_name: productName,
    })
  );
}

export async function maybePromoteDefaultList({
  selectedPersonalLists,
  allowDefaultPromotion,
  currentDefaultListId,
}: {
  selectedPersonalLists: ListData[];
  allowDefaultPromotion: boolean;
  currentDefaultListId: string | undefined;
}): Promise<void> {
  if (
    allowDefaultPromotion &&
    selectedPersonalLists.length === 1 &&
    selectedPersonalLists[0].id !== currentDefaultListId
  ) {
    try {
      await setFavoriteList(selectedPersonalLists[0].id);
    } catch (error) {
      console.error('Error setting favorite list:', error);
    }
  }
}

export function buildSnackbarData({
  listsToAdd,
  idsToRemove,
  productId,
  productName,
  createdList,
  intent,
  allLists,
  multipleListsLabel,
}: {
  listsToAdd: ListData[];
  idsToRemove: string[];
  productId: string;
  productName: string;
  createdList: ListInfo | null;
  intent: 'add' | 'remove';
  allLists: ProductListV2[];
  multipleListsLabel: string;
}): SnackbarData | null {
  const listMap = new Map(allLists.map((l) => [l.id, l]));
  const resolvedAdds = listsToAdd.map((l) => ({ id: l.id, name: l.name }));
  const resolvedRemoves = idsToRemove.map((id) => ({
    id,
    name: listMap.get(id)?.name ?? id,
  }));

  const snackbarInfo = resolveSnackbarInfo({
    listsToAdd: resolvedAdds,
    listsToRemove: resolvedRemoves,
    intention: intent,
    createdNewList: createdList,
    multipleListsLabel,
  });

  if (!snackbarInfo) return null;

  let listSlug = '';
  if (snackbarInfo.type === 'added') {
    if (createdList) {
      listSlug = listMap.get(createdList.id)?.slug ?? '';
    } else if (listsToAdd.length === 1) {
      listSlug = listMap.get(listsToAdd[0].id)?.slug ?? '';
    }
  }

  return { ...snackbarInfo, listSlug, productId, productName, key: Date.now() };
}
